import { ref } from 'vue';
import {
    CONTEXT_CTA_NONE,
    legacyStaticUseFromPieceFunction,
    normalizeChannels,
    normalizeLegacyObjective,
} from '@/constants/creativeContext';

// --- O portão do wizard: a fronteira com o backend, num arquivo só ---
//
// O DESENHO (2026-09-09, palavra do dono do produto): "os insights e neurogens
// ficam travados até o usuário fechar as etapas do modal; todo o resto que pode
// ser gerado é gerado — heatmap, AOIs, neural engagement, etc."
//
// Isto SUBSTITUI o desenho anterior (processar tudo no upload e reprocessar
// depois do wizard). O carimbo `{ wizard_completed: true }` deixou de ser um
// pedido de segunda rodada e passou a ser a CHAVE que abre o portão: sem ele,
// insight e NeuroGen não rodam nem uma vez. Ele sai de "Concluir" — e do avanço
// da etapa 2 quando ela é a última, no vídeo. NÃO sai do fechamento: quem fecha
// a janela no meio está PAUSANDO, e vai voltar.
//
// Voltar é a outra metade. Sem ela o portão seria uma armadilha: a peça ficaria
// parada para sempre e o cliente sem saber por quê. Pior — ela não fica
// invisível, ela MENTE em duas superfícies ao mesmo tempo (o card da listagem
// diz "em processamento", o sino diz "em andamento"), e é o front que produz as
// duas frases. Por isso este contrato é lido nos DOIS lugares.
//
// O CONTRATO (fechado com o backend em 2026-09-09):
//
//   wizard_pending_step: 2 | 3 | null
//
//   2     briefing não enviado  -> reabre na etapa de CONTEXTO, com os campos
//         como ele os deixou (a etapa 2 grava conforme se mexe)
//   3     briefing enviado, camadas por revisar -> reabre nas CAMADAS, sem
//         refazer o que o cliente já escreveu
//   null  encerrado: concluiu, ou é vídeo (que não tem etapa 3), ou é peça de
//         acervo, anterior ao wizard
//
// É DERIVADO no servidor dos dois carimbos que o próprio portão usa
// (`context_submitted_at` e `wizard_completed_at`), não guardado — então não
// dessincroniza, e o front não recalcula etapa nenhuma por conta própria. Vem
// no experimento E no resumo da listagem (`ExperimentSummarySchema`): é
// clicando no card que o cliente volta, então é lá que o campo tem de existir.
//
// NENHUM OUTRO ARQUIVO LÊ CAMPO DE WIZARD DO PAYLOAD. Se o formato mudar, muda
// aqui.

// As etapas em que se pode retomar. A 1 (arquivos e créditos) não é retomável:
// a peça já existe e já foi cobrada, então não há o que selecionar.
export const WIZARD_STEP_BRIEF = 2;
export const WIZARD_STEP_LAYERS = 3;

const RESUMABLE_STEPS = new Set([WIZARD_STEP_BRIEF, WIZARD_STEP_LAYERS]);

// --- O SELO SAI NO CLIQUE, NÃO NO RELÓGIO DA LISTAGEM (2026-09-09) ---
//
// Palavra do dono do produto: "ao clicar em concluir, ainda continua aparecendo
// 'falta você' e demora um tempo pra mudar". O campo que acende o selo vem do
// `/folders`, e o `/folders` só é refeito de tempos em tempos (medido no
// ambiente vivo: a cada ~30 s, quando há job ativo na fila). Entre o clique e o
// próximo rebuild, a tela ficava pedindo ao cliente que fizesse o que ele
// acabara de fazer — pior do que lentidão, é a tela contradizendo o gesto dele.
//
// A trava é MONOTÔNICA, como o `_readyLatched` da prontidão: `wizard_completed`
// não se desfaz no servidor, então uma peça que o cliente carimbou não volta a
// esperar por ele. Isso também a torna imune ao refetch — é justamente uma
// injeção do lado do cliente que o rebuild do `/folders` apagaria, se ela
// morasse no objeto da árvore em vez de aqui.
//
// `ref` e não `Set` cru porque quem lê são computeds de três superfícies (card,
// lista e sino): sem reatividade a trava só apareceria no próximo render por
// outro motivo, que é o defeito de novo, com outro relógio.
const _finishedLocally = ref(new Set());

// --- E O SELO ENTRA NO CLIQUE TAMBÉM: A TRAVA É BIDIRECIONAL (2026-09-09) ---
//
// A trava acima resolvia um sentido só. Palavra do dono do produto, sobre o
// outro: "fui até a fase 3 e antes de processar as camadas eu fechei a aba.
// Entretanto, o criativo estava 'na fila' e não 'falta você', o que me fez
// clicar nele e não cair no modal e sim na tela do criativo."
//
// São dois defeitos com uma raiz só. `wizard_pending_step` mora no `/folders`, e
// MEDIDO no ambiente vivo em 09/09: com a listagem aberta e nada acontecendo, o
// `/folders` não é refeito NENHUMA vez em 95 s. Ele não tem relógio próprio —
// só é refeito quando chega um `status_update` pelo WebSocket (`scheduleWsRefresh`
// na `Main.vue`, com piso de 5 s) ou quando o WS reconecta. Ora, a peça que
// espera o cliente é exatamente a peça cujo pipeline está PARADO atrás do portão:
// ela não emite evento nenhum. A listagem velha não é lenta, é indefinida — dura
// até outra peça produzir um evento, ou até o F5. Foi o F5 que curou o relato.
//
// E quem sabe a verdade primeiro é o front: no instante em que a peça é criada,
// e a cada avanço de etapa, ele SABE que aquela peça tem wizard pendente. Não
// precisa esperar o servidor contar de volta o que ele mesmo acabou de fazer.
//
// O SERVIDOR CONTINUA MANDANDO. Esta memória é consultada num caso só: quando o
// campo está AUSENTE do payload — o card recém-confirmado do upload, cujo item da
// árvore é o otimista com os dados do servidor por cima (`realExperimentData`, em
// `useMediaUpload`, não traz `wizard_pending_step`), ou uma resposta de antes
// desta versão. Se o `/folders` trouxe o campo, é ele que vale, inclusive quando
// vale `null`: quem concluiu o wizard em OUTRA aba tem de ver o selo sumir aqui,
// e memória local velha não pode ressuscitá-lo. Ausência do campo não é o
// servidor dizendo "não" — é a listagem sem a resposta.
const _pendingLocally = ref(new Map());   // id -> etapa em que a peça parou

const _idOf = (experimentId) => (
    experimentId === null || experimentId === undefined ? '' : String(experimentId)
);

export const markWizardPending = (experimentId, step) => {
    const id = _idOf(experimentId);
    if (!id || !RESUMABLE_STEPS.has(step)) return;
    // Carimbada como concluída nesta sessão: não volta a esperar ninguém. Sem
    // esta guarda as duas metades da trava brigariam, e a de trás venceria.
    if (_finishedLocally.value.has(id)) return;
    if (_pendingLocally.value.get(id) === step) return;
    _pendingLocally.value = new Map(_pendingLocally.value).set(id, step);
};

export const markWizardFinished = (experimentId) => {
    const id = _idOf(experimentId);
    if (!id) return;
    // Apagar a memória de espera é o que impede as duas metades de se anularem:
    // concluir é a última palavra, e a etapa em que a peça parou deixa de existir.
    if (_pendingLocally.value.has(id)) {
        const next = new Map(_pendingLocally.value);
        next.delete(id);
        _pendingLocally.value = next;
    }
    if (_finishedLocally.value.has(id)) return;
    _finishedLocally.value = new Set(_finishedLocally.value).add(id);
};

// ÁREAS PRONTAS, SABIDO NESTA SESSÃO (22/09). A listagem só descobre as AOIs no
// rebuild de /folders, e o WebSocket não carrega nada de detecção — então o card
// da peça em etapa 3 ficava dizendo "processando" mesmo com a lista pronta na
// tela do wizard. Quem VIU a lista montada foi o LayersStep; ele grava aqui, e o
// selo "sua vez" lê. Memória de sessão, mesmo contrato das outras duas.
const _areasReadyLocally = ref(new Set());

export const markStep3AreasReady = (experimentId) => {
    const id = _idOf(experimentId);
    if (!id || _areasReadyLocally.value.has(id)) return;
    _areasReadyLocally.value = new Set(_areasReadyLocally.value).add(id);
};

export const step3AreasReadyLocally = (experimentId) => {
    const id = _idOf(experimentId);
    return !!id && _areasReadyLocally.value.has(id);
};

// Só para teste: a trava é de sessão e não tem por que ser desfeita em produção.
export const _resetWizardFinishedLatch = () => {
    _finishedLocally.value = new Set();
    _pendingLocally.value = new Map();
    _areasReadyLocally.value = new Set();
};

// A peça está esperando o cliente? E onde ele parou?
//
// Devolve `null` para tudo o que não está esperando. Silêncio é a resposta
// segura: anunciar "falta você" sobre uma peça de acervo seria a mesma mentira,
// só trocada de lado.
export const readWizardState = (experiment) => {
    if (!experiment || experiment.type === 'folder') return null;
    // Card ainda subindo: o id é `optimistic_<id>` e não existe no servidor.
    if (experiment.isOptimistic) return null;
    const id = String(experiment.id);
    // Carimbado nesta sessão: o servidor ainda pode não ter aparecido na
    // listagem, mas o fato já aconteceu. Ver a trava acima.
    if (_finishedLocally.value.has(id)) return null;

    const step = experiment.wizard_pending_step;
    // O SERVIDOR FALOU — e o que ele diz vence, inclusive o `null`. Só o valor
    // dentro do contrato vira retomada; qualquer outro (0, 1, 4, '2', true) é
    // resposta que não entendemos, e inventar etapa em cima dela seria pior do
    // que calar.
    if (step !== undefined) {
        return RESUMABLE_STEPS.has(step) ? { pending: true, step } : null;
    }

    // Campo AUSENTE: aqui, e só aqui, vale o que esta sessão sabe.
    const local = _pendingLocally.value.get(id);
    return RESUMABLE_STEPS.has(local) ? { pending: true, step: local } : null;
};

// Atalho para as superfícies que só querem saber "esta peça espera o cliente?".
export const isWizardPending = (experiment) => readWizardState(experiment) !== null;

// O contexto GRAVADO da peça, para reabrir a etapa 2 com os campos como o
// cliente os deixou.
//
// "Gravado" passou a ser mais do que "mandado ao avançar" (2026-09-09, palavra
// do dono do produto): a etapa 2 grava campo a campo enquanto ele mexe, então
// estas colunas podem estar preenchidas SEM `context_submitted`. As duas coisas
// são independentes de propósito — a etapa só acaba no avanço, e é o carimbo,
// não o conteúdo, que decide em que etapa a retomada abre.
//
// Vazio aqui é uma RESPOSTA, não um formulário em branco: quem escolheu "Não
// definir" gravou vazio, e é vazio que ele tem de encontrar na volta.
//
// PEÇA LEGADA (uso do estático, 2026-09-29; sem backfill, mapeada na leitura):
//   - só com `context_piece_function` → o uso é DERIVADO (Key Visual → KV;
//     Promocional/Branding → anúncio). A abordagem NÃO é pré-preenchida: seria
//     inferência, não declaração;
//   - objetivo renomeado abre com o nome novo — só na IMAGEM: o vídeo tem lista
//     própria, que ainda usa os nomes antigos;
//   - rótulo "Sem CTA" (que era um rótulo) vira a presença `Sem CTA`.
//
// O USO DERIVADO NÃO É DECLARAÇÃO (review do front, M2): `staticUseDerived`
// marca que o uso veio da função antiga. Enquanto o cliente não tocar nos
// cartões de uso, o formulário não o manda — nem no rascunho, nem no avanço —
// e a função antiga não é limpa. Sem a marca, só avançar trocaria Promocional
// (que o servidor lê como Branding) por `anuncio`, e o perfil da análise mudaria
// sem gesto do cliente.
export const readStoredContext = (experiment) => {
    const legacyLabel = experiment?.context_cta_label || '';
    const legacyNoCta = legacyLabel === CONTEXT_CTA_NONE;
    const storedUse = experiment?.context_static_use || '';
    const isVideo = experiment?.media_type === 'video';
    const objective = experiment?.context_objective || '';
    const derivedUse = storedUse
        ? ''
        : legacyStaticUseFromPieceFunction(experiment?.context_piece_function);
    return {
        staticUse: storedUse || derivedUse,
        staticUseDerived: !!derivedUse,
        objective: isVideo ? objective : normalizeLegacyObjective(objective),
        approach: experiment?.context_approach || '',
        channels: normalizeChannels(experiment?.context_channels),
        ctaPresence: experiment?.context_cta_presence || (legacyNoCta ? CONTEXT_CTA_NONE : ''),
        ctaLabel: legacyNoCta ? '' : legacyLabel,
        tone: experiment?.context_tone || '',
        content: experiment?.context_content || '',
        // Vídeo (SYN-2585). O mapa INVERSO de CONTEXT_PATCH_COLUMNS: sem estas
        // linhas a retomada abriria os campos de vídeo EM BRANCO sobre o que já
        // estava gravado — e o avanço seguinte os mandaria vazios, apagando.
        videoType: experiment?.context_video_type || '',
        platform: experiment?.context_platform || '',
        productionStage: experiment?.context_production_stage || '',
    };
};

// O QUE O SERVIDOR TEM, na forma do formulário — a linha de base do diff do
// rascunho e da memória do avanço na retomada (review do front, M1).
//
// `readStoredContext` é o formulário APRESENTADO: traduz o legado (rótulo "Sem
// CTA" vira presença, objetivo renomeado abre com o nome novo). A base não pode
// ser essa tradução: se ela disser que o rótulo gravado é `''`, escolher "CTA na
// imagem" nunca manda o `''` que limpa o "Sem CTA" antigo, e o servidor fica com
// "CTA na imagem" + "Sem CTA". Aqui cada coluna volta crua.
//
// A exceção é o uso: a base é o uso que o servidor LÊ (o derivado, quando só há
// a função antiga). É contra ele que "Não definir" vira diferença — e viaja com
// a limpeza da função (Q3); com a base crua (`''`) a escolha não sairia.
export const readStoredBaseline = (experiment) => {
    const shown = readStoredContext(experiment);
    return {
        ...shown,
        objective: experiment?.context_objective || '',
        ctaPresence: experiment?.context_cta_presence || '',
        ctaLabel: experiment?.context_cta_label || '',
    };
};
